import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lockup } from "../components/Lockup";
import { RequestInviteModal } from "./AuthModal";

// The public front door — an INVITATION SURFACE, not a funnel (founder ruling 8 Sep §2). There is no
// open sign-up and no waitlist: an account exists only by accepting an invitation, so the button
// records a REQUEST a human then acts on. No price (R3), no seat counts, no scarcity.
//
// v4 Part 2 (30 Sep): light, and one look with the desk. The building lookup and the "ON JUNO ·
// LAST 24H" card are not here — a lookup can only dead-end or disclose someone else's claim, and
// the live card needs numbers we don't have.
//
// THE FIVE AGENTS + the three role cards are the DESIGN FILE's copy, verbatim (founder correction
// 30 Sep: the text is in `Juno Home Light.dc.html` — the page markup and the `roles:` / `steps:`
// lists inside its own script; support.js is only the runtime). The panel carries NO count and NO
// live/not-live claim: a release number is internal and never goes on the public homepage, which
// describes the whole platform, exactly as the film does under its Illustration label.
const AGENTS = [
  { n: "01", name: "The Librarian", verb: "Read",
    desc: "Reads every document that arrives, files it against the right building and holds back anything it can't place.",
    accent: "#8B7BE8", numColor: "#2C2552", tileBg: "#150F24", tileBorder: "#2C2552",
    chipBg: "#14112B", chipBorder: "#2C2552",
    icon: "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" },
  { n: "02", name: "The Underwriter", verb: "Underwrite",
    desc: "Models the building the hour it lands — rent roll, expenses, debt, exit. Every number opens to show its source.",
    accent: "#00D47E", numColor: "#123A28", tileBg: "#0A1410", tileBorder: "#143A2A",
    chipBg: "#0C2419", chipBorder: "#1B4A34",
    icon: "M4 19V5m0 14h16M8 16v-5m4 5V8m4 8v-3" },
  { n: "03", name: "The Matchmaker", verb: "Match",
    desc: "Knows who wants it, at what basis and how fast they move.",
    accent: "#6FB6FF", numColor: "#12303F", tileBg: "#0A1219", tileBorder: "#15304A",
    chipBg: "#0C1B2B", chipBorder: "#1B3C5A",
    icon: "M9 11a3 3 0 100-6 3 3 0 000 6M3 20v-1a4 4 0 014-4h4a4 4 0 014 4v1M17 8a3 3 0 010 6" },
  { n: "04", name: "The Structurer", verb: "Structure",
    desc: "Moves price, terms and leverage until the deal clears for both sides at once.",
    accent: "#C9A6FF", numColor: "#241A38", tileBg: "#100C18", tileBorder: "#2A1F44",
    chipBg: "#171029", chipBorder: "#332656",
    icon: "M4 7h16M4 12h16M4 17h16M8 5v4M16 10v4M11 15v4" },
  { n: "05", name: "The Closer", verb: "Negotiate",
    desc: "Runs the final negotiation until every party can live with the terms, then hands you the phone call.",
    accent: "#FFB020", numColor: "#3A2A0E", tileBg: "#161105", tileBorder: "#3D2C08",
    chipBg: "#20180A", chipBorder: "#4A360C",
    icon: "M4 12l5 5L20 6" },
];

// The three seats carry their own accent in the design file's `roles:` list — green, blue, violet,
// resolved through the `juno-light` token block.
const ROLES = [
  { kicker: "For the owner", title: "Buy, sell or hold on the numbers.",
    points: ["A live mark on every building you own, listed or not",
      "Selling: who wants it, and what they will pay",
      "Buying: say what you're after, and hear when it exists",
      "Hold, trade or refinance on facts, not on a hunch"],
    close: "Run the portfolio on offense.",
    accent: "#00875A", chipBg: "#E4F2EA", chipBorder: "#A5D6BD",
    icon: "M4 21V7l8-4 8 4v14M9 21v-5h6v5" },
  { kicker: "For the broker", title: "Real leads. Not maybes.",
    points: ["Buyers already underwritten, not names on a list",
      "Agents run the sweep and the follow-up for you",
      "Terms pre-tested against debt before you present",
      "Win the listing with demand you can prove"],
    close: "Less time marketing. More time closing.",
    accent: "#1D6FC0", chipBg: "#EAF2FA", chipBorder: "#A9C8E4",
    icon: "M12 3v18M6 7h10l2 2-2 2H6zM18 14H8l-2 2 2 2h10z" },
  { kicker: "For the lender", title: "See the deal before it needs you.",
    points: ["Underwriting arrives complete, not as a stack of PDFs",
      "Sized against your box before it becomes a request",
      "See the financing a deal needs, even when it misses your terms",
      "Fewer dead files. Faster credit decisions."],
    close: "Quote only the deals worth quoting.",
    accent: "#6E45C4", chipBg: "#F1EDFA", chipBorder: "#C6BAE4",
    icon: "M3 10l9-6 9 6M5 10v9h14v-9M9 19v-5h6v5" },
];

// One icon shape, drawn in the seat's or the agent's accent.
function Glyph({ d, colour, size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={colour}
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// The hero film. In the hero it is ~575px wide (a 30% scale), where the in-film type cannot be read,
// so the WHOLE frame is a button into a full-screen player: black ground, the film fit to the screen
// at its native 16:9, sound on (the click is the user gesture that permits it) and restarted from
// 0:00. × or Esc closes it and the hero film goes back to muted autoplay where it left off. A phone
// gets the OS player instead — it is better than anything built in a page, and it is one tap.
function Film({ onFail }) {
  const ref = useRef(null);
  const [sound, setSound] = useState(false);
  const [gone, setGone] = useState(false);
  const [full, setFull] = useState(false);

  // If the film fails, the slot does not render — no placeholder and no empty box, and the text
  // column takes the full width.
  if (gone) return null;

  const toggle = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = sound;                 // autoplay is only allowed muted; the click is the gesture
    if (!sound) v.volume = 0.7;
    setSound(!sound);
    if (v.paused) v.play().catch(() => {});
  };

  const openFull = (e) => {
    if (e) e.stopPropagation();
    const v = ref.current;
    const phone = window.matchMedia("(max-width: 700px)").matches;
    const native = v && (v.webkitEnterFullscreen || v.requestFullscreen);
    if (phone && native) {
      v.muted = false; v.volume = 0.8; v.currentTime = 0; setSound(true);
      try { native.call(v); } catch (_) { /* the play below still gives them the film with sound */ }
      v.play().catch(() => {});
      return;
    }
    if (v) v.pause();
    setFull(true);
  };

  const closeFull = (atTime) => {
    setFull(false);
    const v = ref.current;
    if (!v) return;
    // Esc brings it back to the hero STILL PLAYING (founder, 30 Sep) — and carries the position over,
    // so the film does not jump backwards when the player closes.
    if (typeof atTime === "number" && Number.isFinite(atTime)) v.currentTime = atTime;
    v.muted = true; setSound(false);
    v.play().catch(() => {});
  };

  return (
    <div className="pub-film" data-testid="home-film">
      <div className="pub-film-frame" role="button" tabIndex={0} onClick={openFull}
           aria-label="Watch the film full screen" data-testid="home-film-open"
           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openFull(); } }}>
        <video ref={ref} autoPlay muted loop playsInline preload="metadata"
               poster="/film-poster.jpg" data-testid="home-film-video"
               onError={() => { setGone(true); onFail(); }}>
          {/* VP9 first: some Chromium builds and Linux browsers ship without h264, and this
              component removes itself when nothing decodes. Both formats are offered. */}
          <source src="/juno-platform-film.webm" type="video/webm" />
          <source src="/juno-platform-film.mp4" type="video/mp4" />
        </video>
      </div>
      {/* the two pills live BELOW the frame, left and right, so nothing at all overlays the film
          (founder, 30 Sep). Clicking the video itself still opens full screen. */}
      <div className="pub-film-row">
        <button className="pub-film-btn" onClick={openFull} data-testid="home-film-full">
          <span className="pub-film-play" aria-hidden="true">▶</span> Watch full screen
        </button>
        <button className="pub-film-btn" onClick={toggle} data-testid="home-film-sound">
          {sound ? "Sound off" : "Sound on"}
        </button>
      </div>
      {full && <FullFilm onClose={closeFull} />}
    </div>
  );
}

function FullFilm({ onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(ref.current?.currentTime); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const v = ref.current;
    if (v) { v.currentTime = 0; v.volume = 0.8; v.play().catch(() => {}); }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return (
    <div className="pub-film-full" data-testid="home-film-full-player" role="dialog" aria-modal="true"
         aria-label="Juno platform film">
      <video ref={ref} autoPlay controls playsInline poster="/film-poster.jpg"
             data-testid="home-film-full-video">
        <source src="/juno-platform-film.webm" type="video/webm" />
        <source src="/juno-platform-film.mp4" type="video/mp4" />
      </video>
      <button className="pub-film-x" onClick={() => onClose(ref.current?.currentTime)}
              data-testid="home-film-close" aria-label="Close the film">×</button>
      <span className="pub-film-esc">Esc returns to the page</span>
    </div>
  );
}

export default function Homepage() {
  const [asking, setAsking] = useState(false);
  const [noFilm, setNoFilm] = useState(false);
  const ask = () => setAsking(true);

  return (
    <div className="pub" data-testid="homepage">
      <header className="pub-band" data-juno-ink="dark" data-testid="home-header">
        <div className="pub-wrap pub-top-inner">
          <Link to="/" className="home-brand" aria-label="Juno"><Lockup size="header" /></Link>
          <nav className="pub-nav">
            <a href="#owners" data-testid="home-nav-seats">Owners, brokers, lenders</a>
            <a href="#how" data-testid="home-nav-how">How it works</a>
            <a href="/why-juno" data-testid="home-nav-name">The name</a>
            <Link to="/desk" data-testid="home-nav-signin">Sign in</Link>
            <button className="pub-btn" onClick={ask} data-testid="home-nav-request">
              Request an invitation</button>
          </nav>
        </div>
      </header>

      <section className={"pub-wrap pub-hero" + (noFilm ? " pub-hero-solo" : "")}>
        <div className="pub-hero-text">
          <span className="pub-pill" data-testid="home-pill">
            <span className="pub-pill-dot" />
            <span className="pub-pill-a">Private release</span>
            <span className="pub-pill-div" />
            <span className="pub-pill-b">By invitation</span>
          </span>
          <h1 className="pub-h1" data-testid="home-headline">
            A desk of AI agents that works your commercial real estate deal.
          </h1>
          <p className="pub-lede" data-testid="home-lede">
            To <em className="pub-yes">yes</em>, or an <em className="pub-no">honest no</em>, in hours.
          </p>
          <ul className="pub-steps">
            <li className="pub-step" data-testid="home-step-1">
              <span className="pub-step-n">01</span>
              <span><b>Send a building.</b> An offering memo, rent roll or T-12.</span>
            </li>
            <li className="pub-step" data-testid="home-step-2">
              <span className="pub-step-n">02</span>
              <span><b>The agents work it.</b> They mark it, find who wants it, and move the terms
                until they clear.</span>
            </li>
            <li className="pub-step" data-testid="home-step-3">
              <span className="pub-step-n">03</span>
              <span><b>You decide.</b> Every number shows where it came from, down to the page.</span>
            </li>
          </ul>
          <div className="pub-cta-row">
            <button className="pub-btn" onClick={ask} data-testid="home-hero-request">
              Request an invitation
            </button>
            <Link className="pub-btn-quiet" to="/desk" data-testid="home-hero-signin">
              Already invited? Sign in
            </Link>
          </div>
        </div>
        <Film onFail={() => setNoFilm(true)} />
      </section>

      <section className="pub-sec" id="owners">
        <div className="pub-wrap">
          <div className="pub-sec-head">
            <span className="pub-kicker">Every side of the table</span>
            <h2 className="pub-h2" data-testid="home-roles-headline">One deal. Three winners.</h2>
            <p className="pub-sub" data-testid="home-roles-lede">CRE technology has spent a decade
              arming one side against the others. Juno only works if the owner, the broker and the
              lender all get more than they had.</p>
          </div>
          <div className="pub-role-grid" data-testid="home-roles">
            {ROLES.map((r) => (
              <div className="pub-card" key={r.kicker} style={{ "--accent": r.accent }}
                   data-testid={"home-role-" + r.kicker.split(" ").pop()}>
                <div className="pub-card-top">
                  <span className="pub-card-chip"
                        style={{ background: r.chipBg, borderColor: r.chipBorder }}>
                    <Glyph d={r.icon} colour={r.accent} size={17} />
                  </span>
                  <span className="pub-card-kicker">{r.kicker}</span>
                </div>
                <h3 className="pub-card-h">{r.title}</h3>
                <ul className="pub-points">
                  {r.points.map((p) => <li key={p}>{p}</li>)}
                </ul>
                <span className="pub-card-close">{r.close}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pub-sec" id="how">
        <div className="pub-wrap">
          <div className="pub-sec-head">
            <span className="pub-kicker">How it works</span>
            <h2 className="pub-h2">Five agents, no inbox between them.</h2>
            <p className="pub-sub">They start the hour a building lands and keep going while nobody
              is logged in.</p>
          </div>
          <div className="pub-agents" data-juno-ink="dark" data-testid="home-agents">
            <div className="pub-agents-top">
              <span>The five agents</span>
              <span className="pub-rule" />
            </div>
            <div className="pub-agent-grid">
              {AGENTS.map((a) => (
                <div className="pub-agent" key={a.name}
                     style={{ "--accent": a.accent, "--tile-bg": a.tileBg, "--tile-bd": a.tileBorder }}
                     data-testid={"home-agent-" + a.name.split(" ")[1].toLowerCase()}>
                  <div className="pub-agent-head">
                    <span className="pub-agent-chip"
                          style={{ background: a.chipBg, borderColor: a.chipBorder }}>
                      <Glyph d={a.icon} colour={a.accent} size={16} />
                    </span>
                    <span className="pub-agent-id">
                      <span className="pub-agent-name">{a.name}</span>
                      <span className="pub-agent-verb">{a.verb}</span>
                    </span>
                    <span className="pub-agent-n" style={{ color: a.numColor }}>{a.n}</span>
                  </div>
                  <span className="pub-agent-desc">{a.desc}</span>
                </div>
              ))}
            </div>
            <p className="pub-agents-foot">
              <span className="pub-agents-foot-dot" aria-hidden="true" />
              No schedules and no batches. Each one acts when something needs doing, tells you why,
              and stops rather than guess — you set how far each may go on its own.
            </p>
          </div>
        </div>
      </section>

      <section className="pub-sec">
        <div className="pub-wrap">
          <div className="pub-close-sec">
          <span className="pub-kicker pub-kicker-plain">Not a roadmap</span>
          <h2 className="pub-h2">They are working deals right now.</h2>
          <p className="pub-sub" data-testid="home-closing">
            Juno is in private release. Request an invitation as an owner, a broker or a lender, and
            the agents start on your side of the table the day you join.
          </p>
          <button className="pub-btn" onClick={ask} data-testid="home-closing-request">
            Request an invitation
          </button>
          </div>
        </div>
      </section>

      <footer className="pub-foot-band" data-juno-ink="dark" data-testid="home-footer">
        <div className="pub-wrap">
          <div className="pub-foot-inner">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Lockup size="footer" />
              <a className="pub-btn-quiet" href="/why-juno" data-testid="home-link-whyjuno"
                 style={{ fontSize: 14 }}>Why we are called Juno →</a>
            </div>
            <nav className="pub-foot-nav">
              <a href="/terms" data-testid="home-link-terms">Terms</a>
              <a href="/privacy" data-testid="home-link-privacy">Privacy</a>
              <a href="/sms-consent" data-testid="home-link-sms">SMS consent</a>
              <a href="mailto:support@junocre.com" data-testid="home-foot-email">support@junocre.com</a>
            </nav>
          </div>
          <div className="pub-foot-bottom" data-testid="home-foot-bottom">
            <span>junocre.com</span>
            <span>© 2026 Juno CRE LLC · Houston, Texas</span>
            <span>Juno serves US persons and entities only.</span>
          </div>
        </div>
      </footer>

      {asking && <RequestInviteModal onClose={() => setAsking(false)} />}
    </div>
  );
}
